import { useGetEntityAuditLogs, useGetLeadAuditLogs } from "@/actions/auditLog.actions";
import { AuditLogType } from "@/interfaces";
import { cx, formatDate } from "@/lib/utils";
import { Empty, Modal, Popover, Spin, Table, Typography } from "antd";
import { CalendarIcon, FileEditIcon, HistoryIcon, UserIcon } from "lucide-react";

const { Text } = Typography;

interface AuditLogModalProps {
  leadId?: number;
  entityType?: "lead" | "itinerary" | "booking" | "payment";
  entityId?: string;
  title?: string;
  open: boolean;
  onClose: () => void;
}

const actionColorClassMap: Record<string, string> = {
  create: "text-green-700",
  update: "text-blue-700",
  delete: "text-red-700",
  status_change: "text-orange-700",
};

const getActionColorClass = (action: string) => actionColorClassMap[action] || "";

const getActionIcon = (action: string) => {
  switch (action) {
    case "create":
      return <FileEditIcon className="w-3 h-3" />;
    case "update":
      return <FileEditIcon className="w-3 h-3" />;
    case "delete":
      return <FileEditIcon className="w-3 h-3" />;
    case "status_change":
      return <FileEditIcon className="w-3 h-3" />;
    default:
      return <FileEditIcon className="w-3 h-3" />;
  }
};

const getEntityTypeLabel = (entityType: string) => {
  switch (entityType) {
    case "lead":
      return "Lead";
    case "itinerary":
      return "Itinerary";
    case "booking":
      return "Booking";
    case "payment":
      return "Payment";
    default:
      return entityType;
  }
};

const formatChangeDescription = (log: AuditLogType): string => {
  if (log.description) {
    return log.description;
  }

  const action = log.action.replace("_", " ");
  const entityType = getEntityTypeLabel(log.entityType);

  if (log.fieldName && log.oldValue && log.newValue) {
    return `Changed ${log.fieldName} from "${log.oldValue}" to "${log.newValue}"`;
  } else if (log.fieldName && log.newValue) {
    return `Set ${log.fieldName} to "${log.newValue}"`;
  } else if (log.fieldName) {
    return `Updated ${log.fieldName}`;
  } else {
    return `${action.charAt(0).toUpperCase() + action.slice(1)} ${entityType.toLowerCase()}`;
  }
};

/**
 * Displays audit history for a lead or a generic entity.
 */
export const AuditLogModal: React.FC<AuditLogModalProps> = ({ leadId, entityType, entityId, title, open, onClose }) => {
  const isLeadMode = typeof leadId === "number" && !entityType && !entityId;
  const { data: leadAuditLogsData, isLoading: isLeadLoading, isError: isLeadError } = useGetLeadAuditLogs(
    leadId || 0,
    open && isLeadMode,
  );
  const {
    data: entityAuditLogsData,
    isLoading: isEntityLoading,
    isError: isEntityError,
  } = useGetEntityAuditLogs(entityType || "booking", entityId || "", open && !isLeadMode && !!entityType && !!entityId);

  const auditLogsData = isLeadMode ? leadAuditLogsData : entityAuditLogsData;
  const isLoading = isLeadMode ? isLeadLoading : isEntityLoading;
  const isError = isLeadMode ? isLeadError : isEntityError;
  const modalTitle = title || `${getEntityTypeLabel(isLeadMode ? "lead" : entityType || "booking")} History`;

  const columns = [
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 100,
      render: (action: string) => (
        <div className={cx("flex items-center justify-center space-x-2", getActionColorClass(action))}>
          {getActionIcon(action)}
          <span>{action.replace("_", " ").toUpperCase()}</span>
        </div>
      ),
    },
    {
      title: "Entity",
      dataIndex: "entityType",
      key: "entityType",
      width: 100,
      render: (entityType: string) => <Text className="text-gray-600">{getEntityTypeLabel(entityType)}</Text>,
    },
    {
      title: "Changes",
      key: "changes",
      width: 320,
      render: (record: AuditLogType) => (
        <div className="space-y-1">
          <Text className="text-gray-900">{formatChangeDescription(record)}</Text>
          {record.fieldName && record.oldValue && record.newValue && (
            <div className="text-xs text-gray-500">
              <span className="line-through text-red-500">{record.oldValue}</span>
              {" → "}
              <span className="text-green-600">{record.newValue}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 150,
      render: (user: AuditLogType["user"]) => {
        const displayName = user?.fullname?.trim() ? user.fullname : "System";

        return (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
              <UserIcon className="w-3 h-3 text-gray-600" />
            </div>
            <div>
              <Text className="text-sm font-medium">{displayName}</Text>
            </div>
          </div>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date: string) => (
        <Popover content={formatDate(date, "long")} className="flex items-center gap-1 text-gray-600">
          <CalendarIcon className="w-3 h-3" />
          <Text className="text-xs">{formatDate(date, "summary")}</Text>
        </Popover>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <HistoryIcon className="w-4 h-4 text-purple-600" />
          </div>
          <span className="text-lg font-semibold">{modalTitle}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      styles={{
        body: { padding: "16px 0" },
        header: { paddingBottom: "16px" },
      }}
    >
      <div className="mt-4">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Spin size="large" />
          </div>
        ) : isError ? (
          <div className="text-center py-8">
            <Text type="danger">Failed to load audit logs</Text>
          </div>
        ) : auditLogsData && auditLogsData.data.length > 0 ? (
          <Table
            dataSource={auditLogsData.data}
            columns={columns}
            rowKey="id"
            pagination={{
              total: auditLogsData.total,
              pageSize: auditLogsData.limit,
              current: auditLogsData.page,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            size="small"
            scroll={{ y: 400 }}
            bordered={false}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No audit logs found" className="py-8" />
        )}
      </div>
    </Modal>
  );
};
